/**
 * Copyright 2025 (c) Karsten Röthig, FVA GmbH (for umati and VDW e.V.)
 */
import { DetailAccordionCard, DetailName, Identification, labels } from '@umati/dashboard-common';
import { IMachine, IPositionFair } from '@umati/dashboard-types';
import React, { ReactElement } from 'react';
import Accordion from 'react-bootstrap/Accordion';
import FrontendURLs from 'utilities/FrontendURLs';
import MachineHandling from 'utilities/MachineHandling';
import Header from '../components/header/header';
import { DInfoRects } from '../components/_lib/Details';
import TemplateHelperPubSub from 'utilities/TemplateHelperPubSub';
import {
  useBrowseName,
  useNamespaceIndex,
  usePubSubNode,
  usePubSubNodes,
  usePubSubValue,
} from 'utilities/PubSub/PubSubHooks';
import { TemplateContext } from 'templates/pubSub/types/TemplateContextType';
import { UaVariable } from 'templates/pubSub/types/TemplateType';
import ImageAndTimelinePubSub from 'components/detail/common/ImageAndTimelinePubSub';
import SectionAccordionCards from 'components/detail/template/pubSub/SectionAccordionCards';
import { FilterBrowsename, FilterReferenceAndTypeId, FilterStartNode } from 'utilities/PubSub/NodeFilters';
import { ns0 } from 'utilities/PubSub/PubSubAddressSpace';

const showPubSubDebug = false;

// biome-ignore lint/correctness/noUnusedVariables: Ignore was already here for ESLint
namespace defaults {
  export const machine: IMachine = {
    Id: 361,
    TypeDefinition: 'MachineToolType',
    MachineId: 'clientId=vdw|smplsrv1;nsu=http:_2F_2Fwww.isw.uni-stuttgart.de_2FBasicGMS_2F;i=66382',
    ShortId: 'M361',
    ImagePath: null,
    Data: {
      Model: { text: 'BasicGMS', locale: '' },
      Location: 'INNOTEQ 3.2 B12/VIRTUAL 1 1/N 48.7685303 E 9.1653923',
      DeviceClass: 'CoordinateMeasuringMachine',
      ProductCode: 'CMM_123',
      Manufacturer: { text: 'ISW University of Stuttgart', locale: '' },
      SerialNumber: '2022-8040123099288569160',
      SoftwareRevision: 'v1.05',
      ProductInstanceUri: 'http://www.isw.uni-stuttgart.de/#BasicGMS',
      YearOfConstruction: 2022,
    },
    Online: true,
    Favorite: false,
    ParentId: 'nsu=http:_2F_2Fopcfoundation.org_2FUA_2FMachinery_2F;i=1001',
    ClientId: 'vdw|smplsrv1',
  };
}

function StackElement(props: { stackElementId: any }) {
  const ns = useNamespaceIndex({
    ia: 'http://opcfoundation.org/UA/IA/',
  });

  const childFilters = {
    NumberInList: FilterBrowsename({ Name: 'NumberInList' }),
    IsPartOfBase: FilterBrowsename({
      Name: 'IsPartOfBase',
      NamespaceIndex: ns.ia,
    }),
    SignalOn: FilterBrowsename({ Name: 'SignalOn', NamespaceIndex: ns.ia }),
    SignalMode: FilterBrowsename({ Name: 'SignalMode', NamespaceIndex: ns.ia }),
    SignalColor: FilterBrowsename({
      Name: 'SignalColor',
      NamespaceIndex: ns.ia,
    }),
  };

  const childNodes = usePubSubNode(childFilters, props.stackElementId);
  const childValues = usePubSubValue(childNodes);
  const browseName = useBrowseName(props.stackElementId);

  const browseNameTxt = browseName === undefined ? 'undefined' : browseName.Name;
  return (
    <div>
      <div>{JSON.stringify(browseNameTxt)}</div>
      <div>{JSON.stringify(childValues)}</div>
    </div>
  );
}

interface ITemplateTypeDetailPubSubProps {
  machineId: string;
  typeId: string;
  fairTag?: string;
  machine?: IMachine;
}

function normalizeHeaderValue(rawValue: any, variable?: UaVariable, fallback = 'undefined'): string {
  if (rawValue === null || rawValue === undefined) {
    return fallback;
  }

  const resolvedValue = TemplateHelperPubSub.resolveVariableValue(rawValue, variable);
  const value = resolvedValue?.Value ?? resolvedValue?.text ?? resolvedValue;
  if (value === null || value === undefined) {
    return fallback;
  }

  return String(value);
}

function TemplateTypeDetailPubSub(props: ITemplateTypeDetailPubSubProps): ReactElement {
  const template = TemplateHelperPubSub.getTemplateByTypeId(props.typeId);

  const ns = useNamespaceIndex(template.namespaceUris);
  const templateContext: TemplateContext = { namespaces: ns, template: template };

  const filterInstanceUri = TemplateHelperPubSub.createFilter(ns, template.header.nameVariable?.variablePath);
  const nodeInstanceUri = usePubSubNode(filterInstanceUri);
  const valueInstanceUri = usePubSubValue(nodeInstanceUri);

  const filterModel = TemplateHelperPubSub.createFilter(ns, template.header.modelVariable?.variablePath);
  const nodeModel = usePubSubNode(filterModel);
  const valueModel = usePubSubValue(nodeModel);

  const machine = props.machine; // TODO: PubSub - Basic machine data must come from somewhere
  // The machine (IMachine carries the topic in the MqttTopic property)

  const instanceUri = template.header.nameShowStaticText
    ? template.header.nameStaticText || 'undefined'
    : !valueInstanceUri
      ? MachineHandling.getProductInstanceUri(machine) || 'undefined'
      : valueInstanceUri;
  const model = template.header.modelShowStaticText
    ? template.header.modelStaticText || 'undefined'
    : !valueModel
      ? MachineHandling.getMachineModel(machine)
      : valueModel;
  const instanceUriDisplay = normalizeHeaderValue(instanceUri, template.header.nameVariable);
  const modelDisplay = normalizeHeaderValue(model, template.header.modelVariable);
  const position = props.fairTag ? (machine?.Positions?.[props.fairTag] as IPositionFair) : null;
  const detailRectProps = [
    { label: labels.hall, value: position?.hall },
    { label: labels.booth, value: position?.booth },
  ];
  const anchor = modelDisplay.replace(/\s+/g, '');
  const headerDetail = {
    text: modelDisplay,
    link: FrontendURLs.getMachine(
      {
        TypeDefinition: props.typeId,
        MachineId: props.machineId,
        Encoding: MachineHandling.getEncodingVersion(machine) || 2,
      },
      props.fairTag,
    ),
    anchor,
  };

  // TODO: Remove test code below
  const ns_dbg = useNamespaceIndex({
    mt: 'http://opcfoundation.org/UA/MachineTool/',
    my: 'http://opcfoundation.org/UA/Machinery/',
    di: 'http://opcfoundation.org/UA/DI/',
    ia: 'http://opcfoundation.org/UA/IA/',
    sc: 'http://example.com/ShowcaseMachineTool/',
  });
  //  console.log(ns_dbg)
  const filterStacklight = [
    FilterBrowsename({ Name: 'Monitoring', NamespaceIndex: ns_dbg.mt }),
    FilterBrowsename({ Name: 'Stacklight', NamespaceIndex: ns_dbg.mt }),
  ];

  const filterLoc = [
    FilterBrowsename({ Name: 'Identification', NamespaceIndex: ns_dbg.di }),
    FilterBrowsename({ Name: 'Location', NamespaceIndex: ns_dbg.my }),
  ];

  const filterYear = [
    FilterBrowsename({ Name: 'Identification', NamespaceIndex: ns_dbg.di }),
    FilterBrowsename({ Name: 'YearOfConstruction', NamespaceIndex: ns_dbg.my }),
  ];

  const nodeLocation = usePubSubNode(filterLoc);
  const location = usePubSubValue(nodeLocation);
  const nodeYear = usePubSubNode(filterYear);
  const year = usePubSubValue(nodeYear);

  const nodeStacklight = usePubSubNode(filterStacklight);
  const stackElementNodes = usePubSubNodes(
    FilterReferenceAndTypeId({ Id: ns0.HasOrderedComponent }, { Id: 1006, Namespace: ns_dbg.ia }),
    nodeStacklight,
  );

  const NumberInListFilterObjects = stackElementNodes.map((nId) => ({
    [nId]: [FilterStartNode(nId), FilterBrowsename({ Name: 'NumberInList' })],
  }));

  const NumberInListFilters = Object.assign({}, ...NumberInListFilterObjects);
  const numberInListNodes = usePubSubNode(NumberInListFilters);
  const numberInListValues = usePubSubValue(numberInListNodes);

  // Reverse sorting for demonstration (b and a swapped)
  const stackElementNodesSorted = stackElementNodes.sort((b, a) => numberInListValues[a] - numberInListValues[b]);
  const stackLight = stackElementNodesSorted.map((n) => {
    // console.log(n);
    return <StackElement stackElementId={n} key={n} />;
  });
  const activeProgramCurrentStateFilter = [
    FilterBrowsename({ Name: 'Production', NamespaceIndex: ns_dbg.mt }),
    FilterBrowsename({ Name: 'ActiveProgram', NamespaceIndex: ns_dbg.mt }),
    FilterBrowsename({ Name: 'State', NamespaceIndex: ns_dbg.mt }),
    FilterBrowsename({ Name: 'CurrentState' }), // Ns0
  ];

  const activeProgramCurrentState = usePubSubNode(activeProgramCurrentStateFilter);
  if (activeProgramCurrentState !== undefined) {
    // console.debug('activeProgramCurrentState', activeProgramCurrentState);
  }
  const activeProgramCurrentStateNodes = usePubSubNode(
    {
      Number: FilterBrowsename({ Name: 'Number' }),
      Id: FilterBrowsename({ Name: 'Id' }),
    },
    activeProgramCurrentState,
  );
  const activeProgramCurrentStateValues = usePubSubValue(activeProgramCurrentStateNodes);

  // End TODO

  return (
    <div>
      {props.fairTag && (
        <Header
          {...props}
          fairTag={props.fairTag}
          detail={headerDetail}
          containerClass="container-max"
          listType="machines"
        />
      )}
      <div className="view-machine-detail">
        <div className="hero">
          <div className="container-max">
            <DetailName isOnline={machine?.Online} name={instanceUriDisplay} model={modelDisplay} />
            <DInfoRects infos={detailRectProps} />
          </div>
        </div>
        <div className="container-max">
          {machine && <ImageAndTimelinePubSub machine={machine} templateContext={templateContext} />}
          <div className="row justify-content-end">
            <div className="col-12 offset-lg-2 col-lg-9">
              {showPubSubDebug && (
                <>
                  TODO: Remove next two lines <br></br>
                  ActiveProgram: {JSON.stringify(activeProgramCurrentStateValues)} <br></br>
                  <b>Stacklight</b>: {stackLight}
                  <b>Location</b>: {location} <br></br>
                  <b>Year</b>: {year} <br></br>
                  ENDTODO
                </>
              )}
              {machine && !machine.Online ? (
                <Accordion defaultActiveKey="0">
                  <DetailAccordionCard eventKey="0" title="Identification (offline data)" isExpanded={true}>
                    <Identification {...machine?.Data}></Identification>
                  </DetailAccordionCard>
                </Accordion>
              ) : (
                <SectionAccordionCards templateContext={templateContext} />
              )}
            </div>
            <div className="col-12 col-lg-1" />
          </div>
        </div>
      </div>
    </div>
  );
}

class ErrorBoundary extends React.Component<React.PropsWithChildren<{}>, { hasError: boolean; error: Error | null }> {
  constructor(props: React.PropsWithChildren<{}>) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Error in TemplateTypeDetailPubSub:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="alert alert-danger">
          Something went wrong in the machine detail view. Error: {this.state.error?.message}
        </div>
      );
    }
    return this.props.children;
  }
}

export default class TemplateTypeDetailPubSubWithBoundary extends React.Component<ITemplateTypeDetailPubSubProps> {
  render() {
    return (
      <ErrorBoundary>
        <TemplateTypeDetailPubSub {...this.props} />
      </ErrorBoundary>
    );
  }
}
